/**
 * Client Onboarding Service
 *
 * Handles creation of new clients and provisioning of knowledge base entries
 * Ensures every new client has:
 * - A brand profile
 * - Voice & tone guidelines
 * - Messaging framework
 *
 * Usage:
 *   import { createClient } from '@/services/clientOnboardingService'
 *   const client = await createClient({ name: 'Dallas LIFE' })
 */

import { supabase } from "@/lib/supabaseClient";
import type { Client } from "@/types/client";
import { clientNameToSlug } from "@/types/client";

/**
 * Call the database function to provision knowledge base entries
 * Creates starter records for:
 * - brand_profiles
 * - client_voice
 * - client_messaging
 *
 * @param clientId - UUID of the newly created client
 * @throws Error if database operation fails
 */
export async function onboardClient(clientId: string): Promise<void> {
  const { error } = await supabase.rpc("onboard_client_kb", {
    new_client_id: clientId,
  });

  if (error) {
    console.error("Client onboarding failed:", error);
    throw new Error(`Failed to onboard client: ${error.message}`);
  }

  console.log(`✓ Knowledge base provisioned for client ${clientId}`);
}

/**
 * Create a new client and automatically provision their knowledge base
 *
 * This is the recommended way to create clients in the application.
 * It ensures the client has all necessary KB records from the start.
 *
 * @param clientData - Client creation data
 * @returns Newly created client with KB entries provisioned
 * @throws Error if client creation or KB provisioning fails
 *
 * @example
 * const client = await createClient({
 *   name: 'Dallas LIFE',
 *   shortName: 'DLIFE',
 *   primaryContactName: 'Bob Harvey',
 *   primaryContactEmail: 'bob@dallaslife.org'
 * })
 */
export async function createClient(clientData: {
  name: string;
  shortName?: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
  notes?: string;
  isDemo?: boolean;
}): Promise<Client> {
  // Step 1: Create the client record
  const { data, error } = await supabase
    .from("clients")
    .insert([
      {
        name: clientData.name,
        slug: clientNameToSlug(clientData.name),
        short_name: clientData.shortName ?? null,
        primary_contact_name: clientData.primaryContactName ?? null,
        primary_contact_email: clientData.primaryContactEmail ?? null,
        notes: clientData.notes ?? null,
        is_active: true,
        is_demo: clientData.isDemo ?? false,
        brand: {},
        settings: {},
      },
    ])
    .select()
    .single();

  if (error) {
    console.error("Client creation failed:", error);
    throw new Error(`Failed to create client: ${error.message}`);
  }

  if (!data) {
    throw new Error("Client created but no data returned");
  }

  const newClient: Client = {
    id: data.id,
    name: data.name,
    shortName: data.short_name ?? undefined,
    primaryContactName: data.primary_contact_name ?? undefined,
    primaryContactEmail: data.primary_contact_email ?? undefined,
    notes: data.notes ?? undefined,
    enabledModules: (data.enabled_modules ?? [
      "core",
    ]) as Client["enabledModules"],
    pricingTier: (data.pricing_tier as Client["pricingTier"]) ?? "standard",
    moduleSettings: (data.module_settings as Record<string, unknown>) ?? {},
    createdAt: new Date(data.created_at).getTime(),
    updatedAt: new Date(data.updated_at).getTime(),
  };

  // Step 2: Provision knowledge base
  try {
    await onboardClient(newClient.id);
  } catch (kbError) {
    // Client was created but KB provisioning failed
    // Log the error but don't throw - the client exists and KB can be manually provisioned
    console.error("KB provisioning failed for new client:", kbError);
    console.warn(
      "Client created successfully but knowledge base provisioning failed. You may need to manually provision KB entries.",
    );
  }

  return newClient;
}

/**
 * Check if a client has been fully onboarded (has brand voice + donors)
 *
 * @param clientId - UUID of the client to check
 * @returns Object indicating onboarding completion state
 */
export async function checkOnboardingStatus(clientId: string): Promise<{
  hasBrandVoice: boolean;
  hasDonors: boolean;
  isComplete: boolean;
}> {
  const [brandVoiceResult, donorResult] = await Promise.all([
    supabase
      .from("client_brand_voices" as any)
      .select("id")
      .eq("client_id", clientId)
      .eq("is_active", true)
      .is("deleted_at", null)
      .maybeSingle(),
    supabase
      .from("donors")
      .select("id")
      .eq("client_id", clientId)
      .is("deleted_at", null)
      .limit(1)
      .maybeSingle(),
  ]);

  const hasBrandVoice = !!brandVoiceResult.data;
  const hasDonors = !!donorResult.data;

  return {
    hasBrandVoice,
    hasDonors,
    isComplete: hasBrandVoice && hasDonors,
  };
}

/**
 * Manually provision KB entries for an existing client
 * Use this if a client was created before the auto-provisioning system was added
 *
 * @param clientId - UUID of the client to provision
 */
export async function provisionKnowledgeBase(clientId: string): Promise<void> {
  const status = await checkOnboardingStatus(clientId);

  if (status.isComplete) {
    console.log("Client already has complete knowledge base");
    return;
  }

  await onboardClient(clientId);
}
